import { supabase } from './supabase';

export interface TaskChecklist {
    id: string;
    business_id: string;
    name: string;
    created_at: string;
    updated_at: string;
}

export interface TaskChecklistItem {
    id: string;
    checklist_id: string;
    title: string;
    order_index: number;
    created_at: string;
    updated_at: string;
}

export interface ChecklistItemCompletion {
    id: string;
    item_id: string;
    completed_at: string;
    completed_by: string | null;
}

export interface TaskChecklistWithItems extends TaskChecklist {
    items: (TaskChecklistItem & { completion?: ChecklistItemCompletion })[];
}

export interface CreateChecklistPayload {
    business_id: string;
    name: string;
    items: { title: string }[];
}

export async function createChecklist(payload: CreateChecklistPayload) {
    if (!supabase) throw new Error('Supabase client not initialized');

    try {
        // First create the checklist
        const { data: checklist, error: checklistError } = await supabase
            .from('task_checklists')
            .insert({
                business_id: payload.business_id,
                name: payload.name,
            })
            .select()
            .single();

        if (checklistError) {
            console.error('Failed to create checklist:', checklistError);
            throw checklistError;
        }
        if (!checklist) throw new Error('Failed to create checklist');

        // Then create all the items
        const items = payload.items.map((item, index) => ({
            checklist_id: checklist.id,
            title: item.title,
            order_index: index,
        }));

        const { error: itemsError } = await supabase
            .from('task_checklist_items')
            .insert(items);

        if (itemsError) {
            console.error('Failed to create checklist items:', itemsError);
            throw itemsError;
        }

        return checklist;
    } catch (error) {
        console.error('Error in createChecklist:', error);
        throw error;
    }
}

export async function assignChecklistToTask(taskId: string, checklistId: string) {
    if (!supabase) throw new Error('Supabase client not initialized');

    const { error } = await supabase
        .from('task_checklist_assignments')
        .insert({
            task_id: taskId,
            checklist_id: checklistId,
        });

    if (error) throw error;
}

export async function getChecklistItems(checklistId: string) {
    if (!supabase) throw new Error('Supabase client not initialized');

    const { data, error } = await supabase
        .from('task_checklist_items')
        .select('*')
        .eq('checklist_id', checklistId)
        .order('order_index');

    if (error) throw error;
    return data;
}

export async function getTaskChecklist(taskId: string): Promise<TaskChecklistWithItems | null> {
    if (!supabase) throw new Error('Supabase client not initialized');

    // First get the checklist assignment
    const { data: assignment, error: assignmentError } = await supabase
        .from('task_checklist_assignments')
        .select('id, checklist_id')
        .eq('task_id', taskId)
        .single();

    if (assignmentError || !assignment) return null;

    // Get the checklist with its items
    const { data: checklist, error: checklistError } = await supabase
        .from('task_checklists')
        .select('*')
        .eq('id', assignment.checklist_id)
        .single();

    if (checklistError || !checklist) return null;

    // Get the checklist items
    const { data: items, error: itemsError } = await supabase
        .from('task_checklist_items')
        .select('*')
        .eq('checklist_id', checklist.id)
        .order('order_index');

    if (itemsError) throw itemsError;

    // Get completions for this task's checklist
    const { data: completions, error: completionsError } = await supabase
        .from('task_checklist_item_completions')
        .select('*')
        .eq('assignment_id', assignment.id);

    if (completionsError) throw completionsError;

    // Merge items with their completion status
    const itemsWithCompletions = items.map(item => ({
        ...item,
        completion: completions?.find(c => c.item_id === item.id)
    }));

    return {
        ...checklist,
        items: itemsWithCompletions
    };
}

export async function toggleChecklistItem(
    taskId: string,
    itemId: string,
    resourceId: string | null
) {
    if (!supabase) throw new Error('Supabase client not initialized');

    // Get the assignment ID
    const { data: assignment, error: assignmentError } = await supabase
        .from('task_checklist_assignments')
        .select('id')
        .eq('task_id', taskId)
        .single();

    if (assignmentError || !assignment) throw new Error('Checklist assignment not found');

    // Check if the item is already completed
    const { data: existing, error: existingError } = await supabase
        .from('task_checklist_item_completions')
        .select('id')
        .eq('assignment_id', assignment.id)
        .eq('item_id', itemId)
        .single();

    if (existingError && existingError.code !== 'PGRST116') throw existingError;

    if (existing) {
        // If completed, uncomplete it
        const { error: deleteError } = await supabase
            .from('task_checklist_item_completions')
            .delete()
            .eq('id', existing.id);

        if (deleteError) throw deleteError;
    } else {
        // If not completed, complete it
        const { error: insertError } = await supabase
            .from('task_checklist_item_completions')
            .insert({
                assignment_id: assignment.id,
                item_id: itemId,
                completed_by: resourceId
            });

        if (insertError) throw insertError;
    }
}

export async function isChecklistCompleted(taskId: string): Promise<boolean> {
    if (!supabase) throw new Error('Supabase client not initialized');

    // Get the checklist and its items
    const checklist = await getTaskChecklist(taskId);
    if (!checklist) return true; // If there's no checklist, consider it completed

    // Check if all items have completions
    return checklist.items.every(item => item.completion);
} 